import linkClicked from './links.js';
import { state } from './state.js';
import { elements } from './elements.js';
import { fadeIn, fadeOut } from './animations.js';
import { debounce } from './utils.js';

const updateNavigation = debounce((currentPanel) => {
    const navDots = elements.stickyPanelContainer.querySelectorAll('.sticky-panels__nav .dot');

    // Remove previous active state
    navDots.forEach((dot) => {
        if (dot.classList.contains('is-selected')) dot.classList.remove('is-selected');
    });

    // Get the current panel and select associated nav dot
    const curretNavDot = elements.stickyPanelContainer.querySelector(`[data-panel-ref=${currentPanel.id}]`);
    curretNavDot.classList.add('is-selected');
}, 300, true);

const handleIntersectionObserver = (entries) => {
    const desktopParent = elements.stickyPanelContainer.querySelector('.sticky-panels__desktop-element-container > div');
    const desktopElements = desktopParent.querySelectorAll('.sticky-panels__element-child');

    entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        desktopElements.forEach((el, idx) => {
            if (idx === entry.target.idx) {
                fadeIn(el);
                // el.play();
            } else {
                fadeOut(el);
            }

            updateNavigation(entry.target);
        });
    });
};

const stickyPanelObserver = new IntersectionObserver(handleIntersectionObserver, {
    rootMargin: '-50% 0px',
});

const moveElementsToDesktop = (mobileParents, desktopParent) => {
    for (let i = 0; i < mobileParents.length; i += 1) {
        desktopParent.append(...mobileParents[i].childNodes); // Move all children to desktop parent wrapper
    }
};

const moveElementsToMobile = (mobileParents, desktopParent) => {
    for (let i = 0; i < desktopParent.children.length; i += 1) {
        const childClone = desktopParent.children[i].cloneNode(true); // Need to clone each node before emptying the current parent for indexes to be accurate
        mobileParents[i].append(childClone);
    }
    desktopParent.innerHTML = ''; // Remove all children from the desktop wrapper after cloning
};

const createDesktopElementWrapper = (container) => {
    const existingWrapper = container.querySelector('.sticky-panels__desktop-element-container');

    if (!existingWrapper) { // Check if we already have a wrapper (init on resize)
        const panelContainer = container.querySelector('.sticky-panels__container');
        const wrapper = document.createElement('div');
        const inner = document.createElement('div'); // Need an inner div for anchoring other absolutely-positioned elements
        wrapper.classList.add('sticky-panels__desktop-element-container');
        wrapper.appendChild(inner);
        panelContainer.appendChild(wrapper);
    }
};

const positionStickyElements = (bp, container) => {
    const isLargeDisplay = ['lg', 'xl', 'xxl'].includes(bp);
    const desktopParent = container.querySelector('.sticky-panels__desktop-element-container > div');
    const mobileParents = container.querySelectorAll('.sticky-panels__element');

    // Move elements depending on screen size and whether destop wrapper already has children
    if (isLargeDisplay && !desktopParent.firstChild) moveElementsToDesktop(mobileParents, desktopParent);
    if (!isLargeDisplay && desktopParent.firstChild) moveElementsToMobile(mobileParents, desktopParent);
};

const initNav = (container, panels) => {
    const desktopParent = container.querySelector('.sticky-panels__desktop-element-container');
    const existingNav = container.querySelector('.sticky-panels__nav');

    if (!existingNav) { // Check if we already have a nav (init on resize)
        const panelNavigation = document.createElement('nav');
        panelNavigation.setAttribute('class', 'sticky-panels__nav');

        // Create a new dot for each panel
        panels.forEach((panel, index) => {
            const navLink = document.createElement('a');
            navLink.setAttribute('href', `#panel-${index}`); // Dynamically create href
            navLink.setAttribute('class', 'dot'); // Add class for syling
            navLink.setAttribute('aria-label', `Navigate to slide number ${index + 1}`); // Provide context for screen readers
            navLink.setAttribute('data-panel-ref', `panel-${index}`); // Create a reference point for us to highlight later
            panel.setAttribute('id', `panel-${index}`); // Dynamically assign an id to panel
            panelNavigation.append(navLink); // Add to navigation element
            navLink.addEventListener('click', (e) => linkClicked(e, { disableNavUpdate: true })); // listen for click and use global click handler
        });
        desktopParent.append(panelNavigation);
    }
};

const initStickyPanels = (container) => {
    const panels = container.querySelectorAll('.sticky-panels__panel');

    // Disconnect any existing observers (for resize)
    stickyPanelObserver.disconnect();

    // Create desktop element wrapper DOM nodes
    createDesktopElementWrapper(container);

    // Move elements to correct DOM locations if needed
    positionStickyElements(state.breakpoint, container);

    // Set intersection observers
    panels.forEach((panel, idx) => {
        panel.idx = idx; // Set current index as a custom property for use in observer
        stickyPanelObserver.observe(panel);
    });

    // Create navigation
    initNav(container, panels);
};

export { initStickyPanels as default };
